import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider, ReCaptchaEnterpriseProvider, AppCheck } from 'firebase/app-check';
import { getPerformance, FirebasePerformance } from 'firebase/performance';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase App singleton
export const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Initialize Authentication
export const auth: Auth = getAuth(app);

// Explicitly enforce local persistence so login state survives page refreshes, new tabs, and browser restarts
if (typeof window !== 'undefined') {
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.warn('Firebase Auth setPersistence note:', err);
  });
}

/**
 * Initialize Firebase App Check
 * Protects Firestore, Storage, and Cloud Functions against bots, abuse, and unauthorized replay requests
 */
export let appCheck: AppCheck | null = null;

if (typeof window !== 'undefined') {
  try {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('192.168.');

    // Enable debug token in development or test environments
    if (isLocalhost || (import.meta as any).env?.DEV) {
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    const enterpriseKey =
      (import.meta as any).env?.VITE_RECAPTCHA_ENTERPRISE_KEY ||
      (window as any).__RECAPTCHA_ENTERPRISE_KEY__;

    const reCaptchaSiteKey =
      (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY ||
      (window as any).__RECAPTCHA_SITE_KEY__;

    if (enterpriseKey) {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(enterpriseKey),
        isTokenAutoRefreshEnabled: true,
      });
    } else if (reCaptchaSiteKey) {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(reCaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }
  } catch (err) {
    // Graceful fallback: do not crash application if App Check site key is pending configuration
    console.warn('Firebase App Check initialization note:', err);
  }
}

// Initialize Firebase Performance Monitoring for real-world user speed tracking (FCP, LCP, network)
export let perf: FirebasePerformance | null = null;
if (typeof window !== 'undefined') {
  try {
    perf = getPerformance(app);
  } catch (err) {
    console.debug('Firebase Performance Monitoring note:', err);
  }
}

export default app;
